import { type SurveyAnswerModel } from '~~/package/mapper/survey/model/SurveyResponseModel';
import { type SurveyCompletionModel } from '~~/package/mapper/survey/model/SurveyCompletionModel';

/**
 * 설문 완료 게이팅 순수 로직 + 완료 캐시 팩토리.
 *
 * `7.surveySdk.client.ts` 플러그인 클로저에 갇혀 있던 (surveyId+version) 완료 판정·답변 매핑·
 * 메모이즈 캐시 로직을 추출해 단위 테스트가 가능하게 한다. 동작 의미는 추출 전과 동일하다.
 */

type SurveyAnswers = Record<string, number | string | string[]>;

type AssessmentId = number | string | null | undefined;

/** (surveyId + version + assessmentId) 완료 캐시 키. assessmentId 가 없는(전역) 설문은 'global' 로 대체한다. */
export const completionKey = (surveyId: string, version: string, assessmentId?: AssessmentId): string =>
    `${surveyId}@${version}@${assessmentId ?? 'global'}`;

/** 백엔드 완료 목록을 (key → 최신 제출 시각) Map 으로 변환. 재노출 만료 판정에 createdAt 이 필요하다. */
export const buildCompletionMap = (
    list: ReadonlyArray<{ surveyId: string; version: string; assessmentId?: AssessmentId; createdAt: string }>
): Map<string, string> =>
    new Map(list.map((c) => [completionKey(c.surveyId, c.version, c.assessmentId), c.createdAt]));

/** 완료 후 경과 시간이 재노출 기간(일)을 넘겼는지 판정. reExposureAfterDays 가 없으면 영구 완료(false = 미만료). */
export const isCompletionExpired = (createdAt: string, reExposureAfterDays?: number): boolean => {
    if (!reExposureAfterDays) return false;
    const elapsedMs = Date.now() - new Date(createdAt).getTime();
    return elapsedMs >= reExposureAfterDays * 24 * 60 * 60 * 1000;
};

/**
 * SDK onSubmit 의 answers/config 를 저장 모델의 answers 배열로 매핑한다.
 * config.questions 기준으로 순회해 questionId/type(GA 키)을 보존하고, 미응답 항목은 null.
 */
export const mapSurveyAnswers = (
    answers: SurveyAnswers,
    questions: ReadonlyArray<{ id: string; type: string }>
): SurveyAnswerModel[] =>
    questions.map((q) => ({
        questionId: q.id,
        type: q.type,
        value: answers[q.id] ?? null,
    }));

export interface CompletionCache {
    /** 완료 목록을 1회 로드(메모이즈). 동시 호출은 같은 in-flight 를 공유. */
    ensure: () => Promise<void>;
    /** assessmentId 로 스코프하고, reExposureAfterDays 가 있으면 경과 기간까지 판정한다(만료 시 false). */
    has: (surveyId: string, version: string, assessmentId?: AssessmentId, reExposureAfterDays?: number) => boolean;
    add: (surveyId: string, version: string, assessmentId?: AssessmentId) => void;
    /** 사용자 전환 등으로 캐시를 비워 다음 ensure 때 재요청하게 한다. */
    invalidate: () => void;
    isLoaded: () => boolean;
}

/**
 * 완료 목록 메모이즈 캐시.
 *
 * - `ensure()` 는 최초 1회만 fetch 하고 이후엔 즉시 resolve. 동시 호출은 in-flight Promise 를 공유.
 * - fetch 실패 시 빈 Set 으로 적재해(게이트 미적용 = 설문 노출 유지) 매 조회마다 무한 재요청하는 것을 막는다.
 * - `invalidate()` 로 비우면 다음 `ensure()` 에서 다시 fetch.
 */
export const createCompletionCache = (
    fetchCompletions: () => Promise<SurveyCompletionModel[]>
): CompletionCache => {
    let completedMap: Map<string, string> | null = null;
    let inflight: Promise<void> | null = null;

    const ensure = (): Promise<void> => {
        if (completedMap) return Promise.resolve();
        if (!inflight) {
            inflight = fetchCompletions()
                .then((list) => {
                    completedMap = buildCompletionMap(list);
                })
                .catch((e) => {
                    // 실패 시 게이트 미적용(설문 노출 유지) + 빈 Map 으로 무한 재요청 방지.
                    console.warn('[SV] survey completions fetch 실패:', e);
                    completedMap = new Map();
                })
                .finally(() => {
                    inflight = null;
                });
        }
        return inflight;
    };

    return {
        ensure,
        has: (surveyId, version, assessmentId, reExposureAfterDays) => {
            const createdAt = completedMap?.get(completionKey(surveyId, version, assessmentId));
            if (!createdAt) return false;
            return !isCompletionExpired(createdAt, reExposureAfterDays);
        },
        add: (surveyId, version, assessmentId) => {
            completedMap?.set(completionKey(surveyId, version, assessmentId), new Date().toISOString());
        },
        invalidate: () => {
            completedMap = null;
            inflight = null;
        },
        isLoaded: () => completedMap !== null,
    };
};
